/* ==========================================================================
   TNF backboard geometry 1.2.0                                  2026-09-17
   --------------------------------------------------------------------------
   ONE description of the acrylic backboard outline, shared by the browser
   preview and the server artwork. Both hand this module a raster MASK of the
   full tube envelope (every letter, symbol and its tube thickness, 1 = tube)
   at a known scale, and get back the board outline as an SVG path in
   millimetres, its bounding box and its area.

   1.2.0 - WORD CONNECTORS (PROPOSED geometry, not manufacturer-confirmed).
           A one-piece board must join the words. The caller tells this
           module where the word spaces are (opts.gaps, one per space
           character, in board mm) and where each line's lettering sits
           (opts.lines). For every gap the module reads the ACTUAL ink of the
           two neighbouring words from the mask (the columns just left and
           right of the gap), finds the vertical range where both have
           material, and paints a band of acrylic across the gap there -
           BEFORE the offset and smoothing, so the connector is part of the
           filled geometry, blended by the same margin and (for Cut To Shape)
           the same closing radius as the rest of the board. It is acrylic
           only: no tube, no glow, no length. Two treatments:
             cut_to_shape   tall, full connection: target FINAL height 0.50 H
                            (min 20, max 120 mm), i.e. band = 0.50 H - 2 margin
             cut_to_letter  low, discreet connection: target FINAL height
                            0.22 H (min 12, max 45 mm), band = 0.22 H - 2 margin
           where H is that line's letter height. The band never exceeds the
           vertical extent of the material it joins, it never runs under the
           whole design, and the attachment height follows the letters at
           the gap (a script tail joins at mid-height, block capitals at
           their stems). Lines of a multi-line design that would still be
           separate pieces get one vertical connector each, same rules, at
           the middle of the lines' horizontal overlap. After smoothing the
           module measures the NARROWEST height of each connector in
           millimetres and reports the number of separate pieces. Naked Cut,
           Rectangle, Stand and Box are unchanged. Reference basis: the
           store's style guide shows single words only; its catalogue renders
           ("20% OFF", "30% OFF", "21 Baby") show one-piece Cut To Shape boards
           whose material continues across narrow gaps; no factory drawing of
           a word connector was found. The heights above are a proposal for
           review.

   1.1.1 - minimum feature size: rings (pieces or openings) whose extent is
           under MIN_FEATURE_MM (3 mm) are dropped. They come from raster
           noise at the offset radius (a hole whose inscribed radius is a
           fraction of a pixel over the margin) and cannot be cut; the count
           is reported as droppedTiny. Nothing else changed.

   1.1.0 - Cut To Shape and Cut To Letter re-derived from the store's own
   backboard style-guide pictures (cut-to-shape.png, cut-to-letter.png,
   naked-cut.png on the live creator) and its catalogue product renders,
   measured 17 Sep 2026 relative to the LETTER HEIGHT (H) of the drawn word:
     cut_to_shape   board edge sits a median 0.11 H outside the tube
                    (p10 0.087, p90 0.127), concavities bridged (best-fit
                    closing radius ~0.13 H), NO internal openings - one piece
                    -> margin 0.12 H (min 10 mm, max 40 mm), close 0.15 H
                    (min 15, max 80), holes filled, smoothed
     cut_to_letter  board edge a uniform 0.056 H outside the tube, a plain
                    offset (closing radius 0 fits best), counters and gaps
                    wider than 2 x margin stay OPEN, letters join only where
                    their margins overlap
                    -> margin 0.055 H (min 4 mm, max 15 mm), no closing,
                    holes kept
     naked_cut      0.03 H, near-plain offset, holes kept -> margin 2 mm
                    (unchanged: this is the SUPPORT extent kept in the
                    production spec; the customer preview draws no panel)
   The margins for Cut To Letter and Naked Cut in 1.0.0 (3 / 2 mm) came from
   five recorded orders whose "letter height" was the old page's size-card
   name, not a measurement; the style guide is the only drawing of the
   product, so 1.1.0 follows it and flags the change as PROPOSED.

   Rectangle, Acrylic Stand and Open Box are unchanged.

   Every margin and bridging radius here is PROPOSED geometry: the factory's
   cut margins and construction are unconfirmed, so drawings made from this
   are proposed artwork requiring approval, not CNC-ready files.

   Algorithm: Euclidean distance transform (Felzenszwalb & Huttenlocher, exact,
   O(n)) -> dilation / erosion by radius -> optional hole filling -> marching
   squares contours -> Douglas-Peucker simplification -> optional Chaikin
   smoothing -> path.
   ========================================================================== */
(function (root) {
  'use strict';

  /* Style parameters. margin / close are either millimetres or a function of
     the letter height H (mm) returning millimetres. corner = corner rounding
     for rectangles. fillHoles = the piece has no internal openings. */
  function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }
  var STYLES = {
    cut_to_shape:     { kind: 'shape', margin: function (H) { return clamp(0.12 * H, 10, 40); }, close: function (H) { return clamp(0.15 * H, 15, 80); }, smooth: 3, fillHoles: true,
                        ref: 'style guide cut-to-shape.png: edge 0.11 H from the tube, gaps bridged, one piece' },
    cut_to_letter:    { kind: 'shape', margin: function (H) { return clamp(0.055 * H, 4, 15); }, close: 0, smooth: 1, fillHoles: false,
                        ref: 'style guide cut-to-letter.png: edge 0.056 H from the tube, plain offset, counters open' },
    naked_cut:        { kind: 'shape', margin: 2,  close: 4,  smooth: 1, fillHoles: false,
                        ref: 'support extent only; the preview shows tubing alone' },
    cut_to_rectangle: { kind: 'rect',  margin: 6,  corner: 4 },
    acrylic_stand:    { kind: 'rect',  margin: 10, corner: 4, stand: { depth: 60, plate: 8, width: 0.6 } },
    open_box:         { kind: 'rect',  margin: 10, corner: 0, box: { depth: 70 } }
  };
  /* word / line connectors (PROPOSED): FINAL visible height as a fraction of the
     letter height, clamped in mm; the band painted into the mask is that height
     minus the two margins the offset adds back */
  var CONNECT = {
    cut_to_shape:  { final: 0.50, min: 20, max: 120, minBand: 6, probe: 0.30, note: 'tall, full connection blended into the outline' },
    cut_to_letter: { final: 0.22, min: 12, max: 45,  minBand: 4, probe: 0.30, note: 'low, discreet connection; close-cut look kept' }
  };
  /* resolve a style parameter for a letter height (mm); without a height the
     minimum of the range is used (what 1.0.0 callers get) */
  function param(v, H) { return typeof v === 'function' ? v(H == null ? 0 : H) : v; }
  /* smallest piece or opening drawn (mm): anything smaller is raster noise, not a cut */
  var MIN_FEATURE_MM = 3;

  /* ---- exact Euclidean distance transform ------------------------------ */
  /* f: Float32Array of squared distances (0 for feature pixels, +Inf else). */
  function edt1d(f, n, out, v, z) {
    var k = 0, q, s;
    v[0] = 0; z[0] = -Infinity; z[1] = Infinity;
    for (q = 1; q < n; q++) {
      s = ((f[q] + q * q) - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k]);
      while (s <= z[k]) { k--; s = ((f[q] + q * q) - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k]); }
      k++; v[k] = q; z[k] = s; z[k + 1] = Infinity;
    }
    k = 0;
    for (q = 0; q < n; q++) {
      while (z[k + 1] < q) k++;
      out[q] = (q - v[k]) * (q - v[k]) + f[v[k]];
    }
  }
  /* distance (px) from every pixel to the nearest pixel where mask != 0 */
  function distanceTo(mask, w, h) {
    var n = w * h, f = new Float32Array(n), i;
    for (i = 0; i < n; i++) f[i] = mask[i] ? 0 : 1e20;
    var m = Math.max(w, h), col = new Float32Array(m), out = new Float32Array(m), v = new Int32Array(m), z = new Float32Array(m + 1);
    var x, y;
    for (x = 0; x < w; x++) {                 // columns
      for (y = 0; y < h; y++) col[y] = f[y * w + x];
      edt1d(col, h, out, v, z);
      for (y = 0; y < h; y++) f[y * w + x] = out[y];
    }
    for (y = 0; y < h; y++) {                 // rows
      var row = f.subarray(y * w, y * w + w);
      for (x = 0; x < w; x++) col[x] = row[x];
      edt1d(col, w, out, v, z);
      for (x = 0; x < w; x++) row[x] = Math.sqrt(out[x]);
    }
    return f;                                  // now holds distances
  }
  function dilate(mask, w, h, r) {
    if (r <= 0) return mask.slice ? mask.slice() : new Uint8Array(mask);
    var d = distanceTo(mask, w, h), out = new Uint8Array(w * h);
    for (var i = 0; i < out.length; i++) out[i] = d[i] <= r ? 1 : 0;
    return out;
  }
  function erode(mask, w, h, r) {
    if (r <= 0) return mask.slice ? mask.slice() : new Uint8Array(mask);
    var inv = new Uint8Array(w * h), i;
    for (i = 0; i < inv.length; i++) inv[i] = mask[i] ? 0 : 1;
    var d = distanceTo(inv, w, h), out = new Uint8Array(w * h);
    for (i = 0; i < out.length; i++) out[i] = (mask[i] && d[i] > r) ? 1 : 0;
    return out;
  }

  /* ---- marching squares -> closed rings ------------------------------ */
  /* Returns rings as arrays of [x, y] in pixel units (pixel centres at
     integer + 0.5). Outer rings are clockwise in screen space; holes
     anticlockwise. Standard 16-case table with fragment stitching. */
  var CASES = [
    [], [[[1, 2], [0, 1]]], [[[2, 1], [1, 2]]], [[[2, 1], [0, 1]]],
    [[[1, 0], [2, 1]]], [[[1, 2], [0, 1]], [[1, 0], [2, 1]]], [[[1, 0], [1, 2]]], [[[1, 0], [0, 1]]],
    [[[0, 1], [1, 0]]], [[[1, 2], [1, 0]]], [[[0, 1], [1, 0]], [[2, 1], [1, 2]]], [[[2, 1], [1, 0]]],
    [[[0, 1], [2, 1]]], [[[1, 2], [2, 1]]], [[[0, 1], [1, 2]]], []
  ];
  function rings(mask, w, h) {
    /* pad by one so every feature is enclosed */
    var W = w + 2, H = h + 2, i;
    var v = new Uint8Array(W * H);
    for (var y = 0; y < h; y++) for (var x = 0; x < w; x++) v[(y + 1) * W + x + 1] = mask[y * w + x];
    var fragmentByStart = {}, fragmentByEnd = {}, out = [];
    function key(p) { return p[0] + ',' + p[1]; }
    function stitch(line) {
      var start = line[0], end = line[1], f, g;
      var ks = key(start), ke = key(end);
      if ((f = fragmentByEnd[ks])) {
        delete fragmentByEnd[ks];
        if ((g = fragmentByStart[ke])) {
          delete fragmentByStart[ke];
          if (f === g) { f.ring.push(end); out.push(f.ring); }
          else { f.ring = f.ring.concat(g.ring); f.end = g.end; delete fragmentByStart[key(f.start)]; delete fragmentByEnd[key(g.end)]; fragmentByStart[key(f.start)] = f; fragmentByEnd[key(f.end)] = f; }
        } else { f.ring.push(end); f.end = end; fragmentByEnd[key(end)] = f; }
      } else if ((f = fragmentByStart[ke])) {
        delete fragmentByStart[ke];
        if ((g = fragmentByEnd[ks])) {
          delete fragmentByEnd[ks];
          if (f === g) { f.ring.push(end); out.push(f.ring); }
          else { g.ring = g.ring.concat(f.ring); g.end = f.end; delete fragmentByEnd[key(g.end)]; delete fragmentByStart[key(f.start)]; fragmentByStart[key(g.start)] = g; fragmentByEnd[key(g.end)] = g; }
        } else { f.ring.unshift(start); f.start = start; fragmentByStart[key(start)] = f; }
      } else {
        f = { start: start, end: end, ring: [start, end] };
        fragmentByStart[ks] = f; fragmentByEnd[ke] = f;
      }
    }
    for (var yy = -1; yy < H; yy++) {
      for (var xx = -1; xx < W; xx++) {
        var t0 = (yy >= 0 && xx >= 0 && v[yy * W + xx]) ? 1 : 0;
        var t1 = (yy >= 0 && xx + 1 < W && v[yy * W + xx + 1]) ? 1 : 0;
        var t2 = (yy + 1 < H && xx + 1 < W && v[(yy + 1) * W + xx + 1]) ? 1 : 0;
        var t3 = (yy + 1 < H && xx >= 0 && v[(yy + 1) * W + xx]) ? 1 : 0;
        var c = t0 << 3 | t1 << 2 | t2 << 1 | t3;
        var segs = CASES[c];
        for (i = 0; i < segs.length; i++) {
          var s = segs[i];
          /* segment endpoints are edge midpoints of the 2x2 cell at (xx,yy) */
          stitch([[xx + s[0][0] / 2 + 0.5, yy + s[0][1] / 2 + 0.5], [xx + s[1][0] / 2 + 0.5, yy + s[1][1] / 2 + 0.5]]);
        }
      }
    }
    /* unpad */
    return out.map(function (r) { return r.map(function (p) { return [p[0] - 1, p[1] - 1]; }); });
  }

  function area(ring) { var a = 0; for (var i = 0, j = ring.length - 1; i < ring.length; j = i++) a += (ring[j][0] + ring[i][0]) * (ring[j][1] - ring[i][1]); return a / 2; }

  /* ---- simplify + smooth ---------------------------------------------- */
  function simplify(ring, tol) {
    if (ring.length < 5) return ring;
    /* rings are closed (first point = last point): anchor a second point at
       the far side so the first chord is never degenerate */
    var far = 0, fd = -1;
    for (var q = 1; q < ring.length; q++) { var dd = (ring[q][0] - ring[0][0]) * (ring[q][0] - ring[0][0]) + (ring[q][1] - ring[0][1]) * (ring[q][1] - ring[0][1]); if (dd > fd) { fd = dd; far = q; } }
    var keep = new Uint8Array(ring.length); keep[0] = 1; keep[ring.length - 1] = 1; keep[far] = 1;
    var stack = [[0, far], [far, ring.length - 1]];
    while (stack.length) {
      var seg = stack.pop(), a = seg[0], b = seg[1], best = -1, bd = 0;
      var ax = ring[a][0], ay = ring[a][1], bx = ring[b][0], by = ring[b][1], dx = bx - ax, dy = by - ay, len = Math.hypot(dx, dy) || 1e-9;
      for (var i = a + 1; i < b; i++) {
        var d = Math.abs((ring[i][0] - ax) * dy - (ring[i][1] - ay) * dx) / len;
        if (d > bd) { bd = d; best = i; }
      }
      if (bd > tol && best > 0) { keep[best] = 1; stack.push([a, best], [best, b]); }
    }
    var out = []; for (var k = 0; k < ring.length; k++) if (keep[k]) out.push(ring[k]);
    return out;
  }
  function chaikin(ring, passes) {
    for (var p = 0; p < passes; p++) {
      var out = [], n = ring.length;
      for (var i = 0; i < n; i++) {
        var a = ring[i], b = ring[(i + 1) % n];
        out.push([0.75 * a[0] + 0.25 * b[0], 0.75 * a[1] + 0.25 * b[1]]);
        out.push([0.25 * a[0] + 0.75 * b[0], 0.25 * a[1] + 0.75 * b[1]]);
      }
      ring = out;
    }
    return ring;
  }

  function pathOf(ringsMm, dp) {
    dp = dp == null ? 2 : dp;
    return ringsMm.map(function (r) {
      return 'M' + r.map(function (p) { return p[0].toFixed(dp) + ' ' + p[1].toFixed(dp); }).join('L') + 'Z';
    }).join('');
  }
  function rectPath(x, y, w, h, r, dp) {
    dp = dp == null ? 2 : dp;
    r = Math.max(0, Math.min(r || 0, w / 2, h / 2));
    var f = function (n) { return n.toFixed(dp); };
    if (!r) return 'M' + f(x) + ' ' + f(y) + 'H' + f(x + w) + 'V' + f(y + h) + 'H' + f(x) + 'Z';
    return 'M' + f(x + r) + ' ' + f(y) + 'H' + f(x + w - r) + 'A' + f(r) + ' ' + f(r) + ' 0 0 1 ' + f(x + w) + ' ' + f(y + r) +
      'V' + f(y + h - r) + 'A' + f(r) + ' ' + f(r) + ' 0 0 1 ' + f(x + w - r) + ' ' + f(y + h) +
      'H' + f(x + r) + 'A' + f(r) + ' ' + f(r) + ' 0 0 1 ' + f(x) + ' ' + f(y + h - r) +
      'V' + f(y + r) + 'A' + f(r) + ' ' + f(r) + ' 0 0 1 ' + f(x + r) + ' ' + f(y) + 'Z';
  }

  /* ---- main ------------------------------------------------------------ */
  /* mask: Uint8Array w*h, 1 where the tube (with its thickness) is.
     pxPerMm: mask resolution. origin_mm: [x, y] of the mask's top-left in the
     board coordinate system (board top-left = 0,0). For 'shape' styles the
     mask must include clear space of at least margin + close around the tube
     (the caller pads it), otherwise the offset is clipped. */
  /* opts.clip   {x,y,w,h} mm: mask pixels outside this box are ignored (the
                 geometric tube envelope - removes the antialiasing fringe a
                 raster adds beyond the measured glyph bounds)
     opts.bounds {x,y,w,h} mm: the ordered board box; every outline point is
                 kept inside it, so the drawn board never exceeds the size
                 that was priced and ordered */
  function outline(style, mask, w, h, pxPerMm, origin_mm, opts) {
    var st = STYLES[style] || STYLES.cut_to_shape;
    origin_mm = origin_mm || [0, 0]; opts = opts || {};
    var H = opts.letterHeight_mm, marginMm = param(st.margin, H), closeMm = param(st.close, H);
    if (opts.clip) {
      var c = opts.clip, cx0 = Math.floor((c.x - origin_mm[0]) * pxPerMm), cy0 = Math.floor((c.y - origin_mm[1]) * pxPerMm),
          cx1 = Math.ceil((c.x + c.w - origin_mm[0]) * pxPerMm), cy1 = Math.ceil((c.y + c.h - origin_mm[1]) * pxPerMm);
      var clipped = new Uint8Array(w * h);
      for (var yy = Math.max(0, cy0); yy < Math.min(h, cy1); yy++) for (var xx = Math.max(0, cx0); xx < Math.min(w, cx1); xx++) clipped[yy * w + xx] = mask[yy * w + xx];
      mask = clipped;
    }
    var i, minx = Infinity, miny = Infinity, maxx = -Infinity, maxy = -Infinity;
    for (i = 0; i < w * h; i++) if (mask[i]) { var x = i % w, y = (i - x) / w; if (x < minx) minx = x; if (x > maxx) maxx = x; if (y < miny) miny = y; if (y > maxy) maxy = y; }
    if (minx === Infinity) return { ok: false, reason: 'empty_mask' };
    var env = { x: origin_mm[0] + minx / pxPerMm, y: origin_mm[1] + miny / pxPerMm,
                w: (maxx - minx + 1) / pxPerMm, h: (maxy - miny + 1) / pxPerMm };
    if (st.kind === 'rect') {
      var rx = env.x - marginMm, ry = env.y - marginMm, rw = env.w + 2 * marginMm, rh = env.h + 2 * marginMm;
      return { ok: true, style: style, kind: 'rect', margin_mm: marginMm,
               path: rectPath(rx, ry, rw, rh, st.corner), bbox: { x: rx, y: ry, w: rw, h: rh },
               area_mm2: rw * rh, rings: null, stand: st.stand || null, box: st.box || null, proposed: true };
    }
    var closeR = closeMm * pxPerMm, marginR = marginMm * pxPerMm;
    /* word connectors: bands of acrylic painted into the mask across every word space (PROPOSED) */
    var conn = CONNECT[style] && opts.gaps && opts.gaps.length ? connectors(mask, w, h, pxPerMm, origin_mm, opts, CONNECT[style], marginMm, H) : null;
    if (conn) mask = conn.mask;
    /* closing (bridge gaps) then offset by the margin: dilate by close+margin, erode by close */
    var big = dilate(mask, w, h, closeR + marginR);
    var fin = closeR > 0 ? erode(big, w, h, closeR) : big;
    if (st.fillHoles) fin = fillHoles(fin, w, h);
    /* multi-line designs: if lines are still separate pieces, join consecutive lines with one vertical connector each */
    var lineConn = null;
    if (CONNECT[style] && opts.lines && opts.lines.length > 1 && countPieces(fin, w, h) > 1) {
      lineConn = lineConnectors(mask, w, h, pxPerMm, origin_mm, opts, CONNECT[style], marginMm, H);
      if (lineConn) { mask = lineConn.mask; big = dilate(mask, w, h, closeR + marginR); fin = closeR > 0 ? erode(big, w, h, closeR) : big; if (st.fillHoles) fin = fillHoles(fin, w, h); }
    }
    /* anything still separate (letters spaced wider than two margins, a symbol on its own, ...):
       join the two closest pieces with a short bar of the same band thickness, until one piece (bounded) */
    var extra = [];
    if (CONNECT[style] && opts.connectAll !== false && countPieces(fin, w, h) > 1) {
      /* first pass, deterministic: chain the pieces of each line in reading order (each piece to the next one
         to its right, at their nearest points); then, only if something is still apart, the closest pair */
      var ch = bridgeChain(mask, fin, w, h, pxPerMm, origin_mm, opts, CONNECT[style], marginMm, H);
      if (ch && ch.reports.length) { mask = ch.mask; extra = extra.concat(ch.reports); big = dilate(mask, w, h, closeR + marginR); fin = closeR > 0 ? erode(big, w, h, closeR) : big; if (st.fillHoles) fin = fillHoles(fin, w, h); }
      var guard = 0;
      while (countPieces(fin, w, h) > 1 && guard++ < 12) {
        var br = bridgeClosest(mask, fin, w, h, pxPerMm, origin_mm, CONNECT[style], marginMm, H);
        if (!br) break;
        mask = br.mask; extra.push(br.report);
        big = dilate(mask, w, h, closeR + marginR); fin = closeR > 0 ? erode(big, w, h, closeR) : big; if (st.fillHoles) fin = fillHoles(fin, w, h);
      }
    }
    var pieces = countPieces(fin, w, h);
    /* measure every connector on the FINAL raster: narrowest vertical run across the gap, in mm */
    var connReport = conn ? conn.bands.map(function (b) { return measureBand(fin, w, h, b, pxPerMm, origin_mm); }) : [];
    var lineReport = lineConn ? lineConn.bands.map(function (b) { return measureBandV(fin, w, h, b, pxPerMm, origin_mm); }) : [];
    var rs = rings(fin, w, h);
    var tol = 0.35, sm = st.smooth || 0;
    var minFeaturePx = (opts.minFeature_mm == null ? MIN_FEATURE_MM : opts.minFeature_mm) * pxPerMm, droppedTiny = 0;
    var ringsMm = [], areaPx = 0, bminx = Infinity, bminy = Infinity, bmaxx = -Infinity, bmaxy = -Infinity;
    rs.forEach(function (r) {
      var s = simplify(r, tol); if (sm) s = chaikin(s, sm);
      if (extent(s) < minFeaturePx) { droppedTiny++; return; }   // an opening or piece too small to cut (raster noise at the offset radius)
      var a = area(r); areaPx += a;
      var bd = opts.bounds;
      var mm = s.map(function (p) { var X = origin_mm[0] + p[0] / pxPerMm, Y = origin_mm[1] + p[1] / pxPerMm;
        if (bd) { X = Math.min(bd.x + bd.w, Math.max(bd.x, X)); Y = Math.min(bd.y + bd.h, Math.max(bd.y, Y)); }
        X = Math.round(X * 100) / 100; Y = Math.round(Y * 100) / 100;
        if (X < bminx) bminx = X; if (X > bmaxx) bmaxx = X; if (Y < bminy) bminy = Y; if (Y > bmaxy) bmaxy = Y; return [X, Y]; });
      ringsMm.push(mm);
    });
    return { ok: true, style: style, kind: 'shape', margin_mm: marginMm, close_mm: closeMm, letterHeight_mm: H == null ? null : H, holesFilled: !!st.fillHoles,
             path: pathOf(ringsMm), rings: ringsMm,
             bbox: { x: bminx, y: bminy, w: bmaxx - bminx, h: bmaxy - bminy },
             area_mm2: Math.abs(areaPx) / (pxPerMm * pxPerMm), ringCount: ringsMm.length, droppedTiny: droppedTiny, minFeature_mm: minFeaturePx / pxPerMm,
             pieces: pieces, connectors: connReport, lineConnectors: lineReport, extraConnectors: extra,
             connectorRule: CONNECT[style] ? { final_H: CONNECT[style].final, min_mm: CONNECT[style].min, max_mm: CONNECT[style].max, note: CONNECT[style].note, proposed: true } : null,
             proposed: true };
  }

  /* ---- connectors ------------------------------------------------------ */
  /* ink rows present in a window of columns [x0,x1) limited to rows [y0,y1): returns [minRow,maxRow] or null */
  function inkRows(mask, w, x0, x1, y0, y1) {
    var lo = Infinity, hi = -Infinity, x, y;
    for (y = y0; y < y1; y++) for (x = x0; x < x1; x++) if (mask[y * w + x]) { if (y < lo) lo = y; if (y > hi) hi = y; break; }
    return lo === Infinity ? null : [lo, hi];
  }
  function bandHeightPx(rule, H, marginMm, pxPerMm) {
    var finalMm = clamp(rule.final * (H || 0), rule.min, rule.max);
    var bandMm = Math.max(rule.minBand, finalMm - 2 * marginMm);
    return { finalMm: finalMm, bandMm: bandMm, bandPx: Math.max(1, Math.round(bandMm * pxPerMm)) };
  }
  /* label the 4-connected pieces of a mask within rows [y0,y1): returns { lab: Int32Array (0 = none), n, cx: centroid x per label } */
  function labelPieces(mask, w, h, y0, y1) {
    var lab = new Int32Array(w * h), n = 0, stack = [], sx = [], cnt = [], x, y, p, q;
    for (y = y0; y < y1; y++) for (x = 0; x < w; x++) {
      p = y * w + x; if (!mask[p] || lab[p]) continue;
      n++; lab[p] = n; stack.push(p); sx[n] = 0; cnt[n] = 0;
      while (stack.length) {
        q = stack.pop(); var qx = q % w, qy = (q - qx) / w; sx[n] += qx; cnt[n]++;
        if (qx > 0 && mask[q - 1] && !lab[q - 1]) { lab[q - 1] = n; stack.push(q - 1); }
        if (qx < w - 1 && mask[q + 1] && !lab[q + 1]) { lab[q + 1] = n; stack.push(q + 1); }
        if (qy > y0 && mask[q - w] && !lab[q - w]) { lab[q - w] = n; stack.push(q - w); }
        if (qy < y1 - 1 && mask[q + w] && !lab[q + w]) { lab[q + w] = n; stack.push(q + w); }
      }
    }
    var cx = []; for (var i = 1; i <= n; i++) cx[i] = sx[i] / cnt[i];
    return { lab: lab, n: n, cx: cx };
  }
  /* paint a horizontal band of acrylic across every word space. The material on
     each side of the space is found by labelling the ink of that line: pieces
     whose centre lies left of the space belong to the word before it, the rest
     to the word after. The band sits at the rows where BOTH sides have material
     near the space (so a script tail joins at its own height and block capitals
     at their stems), never taller than the material it joins, and runs from the
     left material to the right material. */
  function connectors(mask, w, h, pxPerMm, origin_mm, opts, rule, marginMm, H) {
    var out = new Uint8Array(mask), bands = [];
    var hb = bandHeightPx(rule, H, marginMm, pxPerMm);
    var probe = Math.max(4, Math.round(rule.probe * (H || 0) * pxPerMm));
    var byLine = {};
    opts.gaps.forEach(function (gp) {
      var y0 = clamp(Math.floor((gp.yTop_mm - origin_mm[1]) * pxPerMm) - 2, 0, h), y1 = clamp(Math.ceil((gp.yBot_mm - origin_mm[1]) * pxPerMm) + 2, 0, h);
      if (y1 <= y0) { bands.push({ gap: gp, skipped: 'no_rows' }); return; }
      var key = y0 + ':' + y1; if (!byLine[key]) byLine[key] = labelPieces(mask, w, h, y0, y1);
      var L = byLine[key], cx = (gp.x_mm - origin_mm[0]) * pxPerMm;
      var wx0 = clamp(Math.round((gp.from_mm - origin_mm[0]) * pxPerMm) - probe, 0, w - 1), wx1 = clamp(Math.round((gp.to_mm - origin_mm[0]) * pxPerMm) + probe, 0, w - 1);
      /* rows and extreme columns of the left-side and right-side material inside the window */
      var aLo = Infinity, aHi = -Infinity, bLo = Infinity, bHi = -Infinity, x, y, p, l;
      for (y = y0; y < y1; y++) for (x = wx0; x <= wx1; x++) { p = y * w + x; l = L.lab[p]; if (!l) continue;
        if (L.cx[l] < cx) { if (y < aLo) aLo = y; if (y > aHi) aHi = y; } else { if (y < bLo) bLo = y; if (y > bHi) bHi = y; } }
      if (aLo === Infinity || bLo === Infinity) { bands.push({ gap: gp, skipped: 'no_material_on_one_side' }); return; }
      var oLo = Math.max(aLo, bLo), oHi = Math.min(aHi, bHi), centre;
      if (oHi >= oLo) centre = (oLo + oHi) / 2; else centre = aHi < bLo ? (aHi + bLo) / 2 : (bHi + aLo) / 2;
      var uLo = Math.min(aLo, bLo), uHi = Math.max(aHi, bHi);
      var half = hb.bandPx / 2, top = Math.max(uLo, Math.round(centre - half)), bot = Math.min(uHi, Math.round(centre + half));
      if (oHi < oLo) { top = Math.min(top, Math.min(aHi, bHi)); bot = Math.max(bot, Math.max(aLo, bLo)); }   // no common rows: reach both ends
      /* attachment columns: the left material's rightmost pixel and the right material's leftmost pixel within the band rows */
      var xa = -1, xb = w;
      for (y = top; y <= bot; y++) for (x = wx0; x <= wx1; x++) { p = y * w + x; l = L.lab[p]; if (!l) continue; if (L.cx[l] < cx) { if (x > xa) xa = x; } else { if (x < xb) xb = x; } }
      var attached = xa >= 0 && xb < w;
      if (xa < 0) xa = Math.round(cx) - 1; if (xb >= w) xb = Math.round(cx) + 1;
      var xs = Math.max(0, Math.min(xa, xb) - 1), xe = Math.min(w - 1, Math.max(xa, xb) + 1);
      for (y = top; y <= bot; y++) for (x = xs; x <= xe; x++) out[y * w + x] = 1;
      bands.push({ gap: gp, xl: Math.min(xa, xb), xr: Math.max(xa, xb), top: top, bot: bot, centre: centre, bandMm: hb.bandMm, targetFinalMm: hb.finalMm, attached: attached, overlapRows: oHi >= oLo });
    });
    return { mask: out, bands: bands };
  }
  /* one vertical band between consecutive lines, at the middle of their horizontal overlap */
  function lineConnectors(mask, w, h, pxPerMm, origin_mm, opts, rule, marginMm, H) {
    var out = new Uint8Array(mask), bands = [];
    var hb = bandHeightPx(rule, H, marginMm, pxPerMm);
    var L = opts.lines.slice().sort(function (a, b) { return a.yTop_mm - b.yTop_mm; });
    for (var i = 0; i + 1 < L.length; i++) {
      var a = L[i], b = L[i + 1];
      var ox0 = Math.max(a.xL_mm, b.xL_mm), ox1 = Math.min(a.xR_mm, b.xR_mm);
      var cxMm = ox1 > ox0 ? (ox0 + ox1) / 2 : (Math.max(a.xR_mm, b.xR_mm) + Math.min(a.xL_mm, b.xL_mm)) / 2;
      var cx = Math.round((cxMm - origin_mm[0]) * pxPerMm);
      var yTop = Math.round((a.yBot_mm - origin_mm[1]) * pxPerMm), yBot = Math.round((b.yTop_mm - origin_mm[1]) * pxPerMm);
      /* find the actual material above and below the inter-line gap in the band's columns */
      var half = hb.bandPx / 2, x0 = clamp(Math.round(cx - half), 0, w - 1), x1 = clamp(Math.round(cx + half), 0, w - 1);
      var yA = -1, yB = -1, y, x, hit;
      for (y = clamp(yTop + 2, 0, h - 1); y >= 0 && yA < 0; y--) { hit = false; for (x = x0; x <= x1; x++) if (mask[y * w + x]) { hit = true; break; } if (hit) yA = y; }
      for (y = clamp(yBot - 2, 0, h - 1); y < h && yB < 0; y++) { hit = false; for (x = x0; x <= x1; x++) if (mask[y * w + x]) { hit = true; break; } if (hit) yB = y; }
      if (yA < 0 || yB < 0 || yB <= yA) { bands.push({ between: [i, i + 1], skipped: 'no_material_in_column' }); continue; }
      for (y = yA; y <= yB; y++) for (x = x0; x <= x1; x++) out[y * w + x] = 1;
      bands.push({ between: [i, i + 1], cx: cx, x0: x0, x1: x1, yA: yA, yB: yB, bandMm: hb.bandMm, targetFinalMm: hb.finalMm });
    }
    return { mask: out, bands: bands };
  }
  /* nearest boundary points of two labelled pieces (exact, over their boundary pixels) */
  function nearestPair(bdA, bdB, w) {
    var best = null, i, j, pa, pb, ax, ay, bx, by, d;
    /* coarse: sample B every 3rd pixel, then refine around the best */
    for (i = 0; i < bdA.length; i++) { pa = bdA[i]; ax = pa % w; ay = (pa - ax) / w;
      for (j = 0; j < bdB.length; j += 3) { pb = bdB[j]; bx = pb % w; by = (pb - bx) / w; d = (ax - bx) * (ax - bx) + (ay - by) * (ay - by); if (best === null || d < best.d) best = { d: d, a: pa, b: pb }; } }
    if (!best) return null;
    ax = best.a % w; ay = (best.a - ax) / w;
    for (j = 0; j < bdB.length; j++) { pb = bdB[j]; bx = pb % w; by = (pb - bx) / w; d = (ax - bx) * (ax - bx) + (ay - by) * (ay - by); if (d < best.d) best = { d: d, a: best.a, b: pb }; }
    return best;
  }
  function paintBar(out, w, h, ax, ay, bx, by, r, ext) {
    var len = Math.max(1, Math.hypot(bx - ax, by - ay)), ux = (bx - ax) / len, uy = (by - ay) / len;
    var x0 = ax - ux * ext, y0 = ay - uy * ext, x1 = bx + ux * ext, y1 = by + uy * ext;
    var minX = clamp(Math.floor(Math.min(x0, x1) - r - 1), 0, w - 1), maxX = clamp(Math.ceil(Math.max(x0, x1) + r + 1), 0, w - 1);
    var minY = clamp(Math.floor(Math.min(y0, y1) - r - 1), 0, h - 1), maxY = clamp(Math.ceil(Math.max(y0, y1) + r + 1), 0, h - 1);
    var vx = x1 - x0, vy = y1 - y0, vv = vx * vx + vy * vy || 1, x, y;
    for (y = minY; y <= maxY; y++) for (x = minX; x <= maxX; x++) {
      var t = ((x - x0) * vx + (y - y0) * vy) / vv; t = t < 0 ? 0 : t > 1 ? 1 : t;
      var cxp = x0 + t * vx, cyp = y0 + t * vy; if ((x - cxp) * (x - cxp) + (y - cyp) * (y - cyp) <= r * r) out[y * w + x] = 1;
    }
  }
  /* chain the separate pieces of each line in reading order: piece i joins piece i+1 at their nearest points */
  function bridgeChain(mask, fin, w, h, pxPerMm, origin_mm, opts, rule, marginMm, H) {
    var L = labelPieces(fin, w, h, 0, h); if (L.n < 2) return null;
    var bd = [], cy = [], cnt = [], i, x, y, p, l;
    for (i = 1; i <= L.n; i++) { bd[i] = []; cy[i] = 0; cnt[i] = 0; }
    for (y = 0; y < h; y++) for (x = 0; x < w; x++) { p = y * w + x; l = L.lab[p]; if (!l) continue; cy[l] += y; cnt[l]++;
      if (x === 0 || y === 0 || x === w - 1 || y === h - 1 || !fin[p - 1] || !fin[p + 1] || !fin[p - w] || !fin[p + w]) bd[l].push(p); }
    /* line of each piece: the opts.lines box holding its centroid y (nearest otherwise) */
    var lines = (opts.lines || []).map(function (ln) { return { top: (ln.yTop_mm - origin_mm[1]) * pxPerMm, bot: (ln.yBot_mm - origin_mm[1]) * pxPerMm }; });
    function lineOf(yc) { if (!lines.length) return 0; var best = 0, bd2 = Infinity; for (var k = 0; k < lines.length; k++) { var d = yc < lines[k].top ? lines[k].top - yc : yc > lines[k].bot ? yc - lines[k].bot : 0; if (d < bd2) { bd2 = d; best = k; } } return best; }
    var pieces = []; for (i = 1; i <= L.n; i++) pieces.push({ id: i, cx: L.cx[i], cy: cy[i] / cnt[i], line: lineOf(cy[i] / cnt[i]) });
    pieces.sort(function (a, b) { return a.line - b.line || a.cx - b.cx; });
    var hb = bandHeightPx(rule, H, marginMm, pxPerMm), r = Math.max(1, hb.bandPx / 2), ext = Math.ceil(marginMm * pxPerMm) + 2;
    var out = new Uint8Array(mask), reports = [];
    for (i = 0; i + 1 < pieces.length; i++) {
      var a = pieces[i], b = pieces[i + 1]; if (a.line !== b.line) continue;
      var np = nearestPair(bd[a.id], bd[b.id], w); if (!np) continue;
      var ax = np.a % w, ay = (np.a - ax) / w, bx = np.b % w, by = (np.b - bx) / w;
      paintBar(out, w, h, ax, ay, bx, by, r, ext);
      reports.push({ from_mm: [Math.round((origin_mm[0] + ax / pxPerMm) * 10) / 10, Math.round((origin_mm[1] + ay / pxPerMm) * 10) / 10], to_mm: [Math.round((origin_mm[0] + bx / pxPerMm) * 10) / 10, Math.round((origin_mm[1] + by / pxPerMm) * 10) / 10],
                     gap_mm: Math.round(Math.sqrt(np.d) / pxPerMm * 10) / 10, bar_mm: Math.round(hb.bandMm * 10) / 10, targetFinal_mm: Math.round(hb.finalMm * 10) / 10, reason: 'adjacent_pieces_in_reading_order' });
    }
    return { mask: out, reports: reports };
  }
  /* join the two closest separate pieces of the FINAL board with a bar painted into the ink mask */
  function bridgeClosest(mask, fin, w, h, pxPerMm, origin_mm, rule, marginMm, H) {
    var L = labelPieces(fin, w, h, 0, h); if (L.n < 2) return null;
    /* boundary pixels per piece */
    var bd = []; for (var i = 1; i <= L.n; i++) bd[i] = [];
    var x, y, p, l;
    for (y = 0; y < h; y++) for (x = 0; x < w; x++) { p = y * w + x; l = L.lab[p]; if (!l) continue;
      if (x === 0 || y === 0 || x === w - 1 || y === h - 1 || !fin[p - 1] || !fin[p + 1] || !fin[p - w] || !fin[p + w]) bd[l].push(p); }
    /* nearest pair: distance transform of each piece, evaluated on the others' boundary pixels (coarse then exact) */
    var best = null;
    for (i = 1; i <= L.n; i++) {
      var one = new Uint8Array(w * h); for (var k = 0; k < bd[i].length; k++) one[bd[i][k]] = 1;
      var d = distanceTo(one, w, h);
      for (var j = 1; j <= L.n; j++) { if (j === i) continue;
        for (k = 0; k < bd[j].length; k++) { p = bd[j][k]; if (best === null || d[p] < best.d) best = { d: d[p], a: i, b: j, pb: p }; } }
    }
    if (!best) return null;
    /* exact nearest pixel of piece a to pb */
    var bx = best.pb % w, by = (best.pb - bx) / w, pa = -1, dm = Infinity;
    for (k = 0; k < bd[best.a].length; k++) { p = bd[best.a][k]; var px = p % w, py = (p - px) / w, dd = (px - bx) * (px - bx) + (py - by) * (py - by); if (dd < dm) { dm = dd; pa = p; } }
    var ax = pa % w, ay = (pa - ax) / w;
    var hb = bandHeightPx(rule, H, marginMm, pxPerMm), r = Math.max(1, hb.bandPx / 2);
    /* extend the bar past both ends by the margin so it overlaps the ink behind each board edge */
    var ext = Math.ceil(marginMm * pxPerMm) + 2, len = Math.max(1, Math.sqrt(dm)), ux = (bx - ax) / len, uy = (by - ay) / len;
    var x0 = ax - ux * ext, y0 = ay - uy * ext, x1 = bx + ux * ext, y1 = by + uy * ext;
    var out = new Uint8Array(mask);
    var minX = clamp(Math.floor(Math.min(x0, x1) - r - 1), 0, w - 1), maxX = clamp(Math.ceil(Math.max(x0, x1) + r + 1), 0, w - 1);
    var minY = clamp(Math.floor(Math.min(y0, y1) - r - 1), 0, h - 1), maxY = clamp(Math.ceil(Math.max(y0, y1) + r + 1), 0, h - 1);
    var vx = x1 - x0, vy = y1 - y0, vv = vx * vx + vy * vy || 1;
    for (y = minY; y <= maxY; y++) for (x = minX; x <= maxX; x++) {
      var t = ((x - x0) * vx + (y - y0) * vy) / vv; t = t < 0 ? 0 : t > 1 ? 1 : t;
      var cxp = x0 + t * vx, cyp = y0 + t * vy; if ((x - cxp) * (x - cxp) + (y - cyp) * (y - cyp) <= r * r) out[y * w + x] = 1;
    }
    return { mask: out, report: { from_mm: [Math.round((origin_mm[0] + ax / pxPerMm) * 10) / 10, Math.round((origin_mm[1] + ay / pxPerMm) * 10) / 10],
                                  to_mm: [Math.round((origin_mm[0] + bx / pxPerMm) * 10) / 10, Math.round((origin_mm[1] + by / pxPerMm) * 10) / 10],
                                  gap_mm: Math.round(Math.sqrt(dm) / pxPerMm * 10) / 10, bar_mm: Math.round(hb.bandMm * 10) / 10, targetFinal_mm: Math.round(hb.finalMm * 10) / 10, reason: 'pieces_still_separate' } };
  }
  /* narrowest vertical run of final material through the band, across the gap columns */
  function measureBand(fin, w, h, b, pxPerMm, origin_mm) {
    if (b.skipped) return { x_mm: b.gap.x_mm, skipped: b.skipped };
    var cy = Math.round(b.centre), minRun = Infinity, minX = -1, x, y;
    for (x = b.xl; x <= b.xr; x++) {
      if (!fin[cy * w + x]) { minRun = 0; minX = x; break; }
      var top = cy, bot = cy;
      while (top > 0 && fin[(top - 1) * w + x]) top--;
      while (bot < h - 1 && fin[(bot + 1) * w + x]) bot++;
      var run = bot - top + 1; if (run < minRun) { minRun = run; minX = x; }
    }
    return { x_mm: Math.round((origin_mm[0] + (b.xl + b.xr) / 2 / pxPerMm) * 10) / 10, gapWidth_mm: Math.round((b.xr - b.xl) / pxPerMm * 10) / 10,
             centreY_mm: Math.round((origin_mm[1] + b.centre / pxPerMm) * 10) / 10, band_mm: Math.round(b.bandMm * 10) / 10, targetFinal_mm: Math.round(b.targetFinalMm * 10) / 10,
             narrowest_mm: minRun === Infinity ? null : Math.round(minRun / pxPerMm * 10) / 10, narrowestAtX_mm: minX < 0 ? null : Math.round((origin_mm[0] + minX / pxPerMm) * 10) / 10, connected: minRun > 0 && minRun !== Infinity, attachedToInk: !!b.attached, materialOverlapsInX: b.xr - b.xl <= 2 };
  }
  function measureBandV(fin, w, h, b, pxPerMm, origin_mm) {
    if (b.skipped) return { between: b.between, skipped: b.skipped };
    var cx = Math.round(b.cx), minRun = Infinity, x, y;
    for (y = b.yA + 1; y < b.yB; y++) {
      if (!fin[y * w + cx]) { minRun = 0; break; }
      var l = cx, r = cx;
      while (l > 0 && fin[y * w + l - 1]) l--;
      while (r < w - 1 && fin[y * w + r + 1]) r++;
      var run = r - l + 1; if (run < minRun) minRun = run;
    }
    return { between: b.between, x_mm: Math.round((origin_mm[0] + b.cx / pxPerMm) * 10) / 10, band_mm: Math.round(b.bandMm * 10) / 10, targetFinal_mm: Math.round(b.targetFinalMm * 10) / 10,
             narrowest_mm: minRun === Infinity ? null : Math.round(minRun / pxPerMm * 10) / 10, connected: minRun > 0 && minRun !== Infinity };
  }
  /* number of separate pieces of material (4-connected flood fill) */
  function countPieces(mask, w, h) {
    var seen = new Uint8Array(w * h), n = 0, stack = [], i, p;
    for (i = 0; i < w * h; i++) {
      if (!mask[i] || seen[i]) continue;
      n++; seen[i] = 1; stack.push(i);
      while (stack.length) {
        p = stack.pop(); var x = p % w, y = (p - x) / w;
        if (x > 0 && mask[p - 1] && !seen[p - 1]) { seen[p - 1] = 1; stack.push(p - 1); }
        if (x < w - 1 && mask[p + 1] && !seen[p + 1]) { seen[p + 1] = 1; stack.push(p + 1); }
        if (y > 0 && mask[p - w] && !seen[p - w]) { seen[p - w] = 1; stack.push(p - w); }
        if (y < h - 1 && mask[p + w] && !seen[p + w]) { seen[p + w] = 1; stack.push(p + w); }
      }
    }
    return n;
  }
  /* largest side of a ring's bounding box, in raster px */
  function extent(r) {
    var minx = Infinity, miny = Infinity, maxx = -Infinity, maxy = -Infinity;
    for (var i = 0; i < r.length; i++) { var p = r[i]; if (p[0] < minx) minx = p[0]; if (p[0] > maxx) maxx = p[0]; if (p[1] < miny) miny = p[1]; if (p[1] > maxy) maxy = p[1]; }
    return Math.max(maxx - minx, maxy - miny);
  }

  /* Convenience for callers that only have the envelope box (no mask), used
     for the quote's board size before any raster exists: board = envelope +
     2 x margin for every style. */
  function marginFor(style, H) { return param((STYLES[style] || STYLES.cut_to_shape).margin, H); }
  function closeFor(style, H) { var st = STYLES[style] || STYLES.cut_to_shape; return st.kind === 'shape' ? param(st.close, H) : 0; }
  /* clear space the raster needs around the envelope for this style */
  function padFor(style, H) { var st = STYLES[style] || STYLES.cut_to_shape; return st.kind === 'shape' ? marginFor(style, H) + closeFor(style, H) : marginFor(style, H); }
  /* the largest margin the style can take (for callers sizing a raster before H is known) */
  function padMax(style) { var st = STYLES[style] || STYLES.cut_to_shape; return st.kind === 'shape' ? param(st.margin, 1e6) + param(st.close, 1e6) : param(st.margin, 1e6); }

  /* fill every enclosed hole: flood the background from the raster border,
     everything not reached is part of the piece */
  function fillHoles(mask, w, h) {
    var out = new Uint8Array(w * h), seen = new Uint8Array(w * h), stack = [], i, x, y;
    function push(p) { if (!seen[p] && !mask[p]) { seen[p] = 1; stack.push(p); } }
    for (x = 0; x < w; x++) { push(x); push((h - 1) * w + x); }
    for (y = 0; y < h; y++) { push(y * w); push(y * w + w - 1); }
    while (stack.length) {
      var p = stack.pop(); x = p % w; y = (p - x) / w;
      if (x > 0) push(p - 1); if (x < w - 1) push(p + 1); if (y > 0) push(p - w); if (y < h - 1) push(p + w);
    }
    for (i = 0; i < w * h; i++) out[i] = seen[i] ? 0 : 1;
    return out;
  }

  var API = { version: '1.2.0', MIN_FEATURE_MM: MIN_FEATURE_MM, CONNECT: CONNECT, STYLES: STYLES, countPieces: countPieces, outline: outline, marginFor: marginFor, closeFor: closeFor, padFor: padFor, padMax: padMax, fillHoles: fillHoles,
              distanceTo: distanceTo, dilate: dilate, erode: erode, rings: rings, simplify: simplify, chaikin: chaikin, rectPath: rectPath, area: area, proposed: true };
  root.TNF_BOARD = API;
  if (typeof module !== 'undefined' && module.exports) module.exports = API;
})(typeof window !== 'undefined' ? window : globalThis);
